import {Component, EventEmitter, forwardRef, Input, type OnChanges, Output, type SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {finalize} from 'rxjs';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {SustainabilityRatingType} from '@app/shared/services/api/sustainability-ratings/ratings/sustainability-ratings.interface';
import {SustainabilityRatingTypeService} from '@app/shared/services/api/sustainability-ratings/sustainability-rating-types.service';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {MessageTranslateService} from "@shared/services";

export enum SustainabilityRatingFilterOptions {
  ALL_RATINGS = 'all_ratings',
  RATINGS_EXPIRING_SOON = 'ratings_expiring_soon'
}

interface FilterOption {
  value: number | SustainabilityRatingFilterOptions;
  label: string;
}


@Component({
  selector: 'app-sustainability-rating-type-select',
  templateUrl: './sustainability-rating-type-select.component.html',
  styleUrls: ['./sustainability-rating-type-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SustainabilityRatingTypeSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SustainabilityRatingTypeSelectComponent implements OnChanges, ControlValueAccessor {
  @Input() options: SustainabilityRatingType[];
  @Input() placeholder: I18nMessage | string = {messageKey: "Sustainability Rating Types"};
  @Input() loading = false;
  @Input() hideSelected = true;
  @Input() appendTo: string;
  @Input() enableFilterOptions = false;
  @Input() clearable = false;
  @Input() readOnly = false;

  @Output() dataLoaded = new EventEmitter<SustainabilityRatingType[]>();
  @ViewChild(AceSelectComponent, {static: true})
  private aceSelect: AceSelectComponent;

  private readonly presetFilterOptions: FilterOption[] = [
    {
      value: SustainabilityRatingFilterOptions.ALL_RATINGS,
      label: this.messageTranslateService.instant('All Ratings')
    },
    {
      value: SustainabilityRatingFilterOptions.RATINGS_EXPIRING_SOON,
      label: this.messageTranslateService.instant('Ratings Expiring soon')
    }
  ];

  allOptions: FilterOption[];

  constructor(private messageTranslateService: MessageTranslateService,
              private sustainabilityRatingTypeService: SustainabilityRatingTypeService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.enableFilterOptions || changes.options) {
      if (this.options == null) {
        this.loadOptions(this.enableFilterOptions);
      } else {
        this.allOptions = this.buildAllOptions(this.enableFilterOptions, this.options);
      }
    }
  }

  writeValue(obj: number | SustainabilityRatingFilterOptions): void {
    this.aceSelect.writeValue(obj);
  }

  registerOnChange(fn: (option: number | SustainabilityRatingFilterOptions) => void): void {
    this.aceSelect.registerOnChange(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.aceSelect.registerOnTouched(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  private loadOptions(enableFilterOptions: boolean): void {
    this.loading = true;
    this.sustainabilityRatingTypeService.getAllSustainabilityRatingTypes()
      .pipe(finalize(() => this.loading = false))
      .subscribe(sustainabilityRatingTypes => {
        this.options = sustainabilityRatingTypes;
        this.allOptions = this.buildAllOptions(enableFilterOptions, sustainabilityRatingTypes);
        this.dataLoaded.emit(sustainabilityRatingTypes);
      });
  }

  private buildAllOptions(enableFilterMode: boolean, sustainabilityRatingTypes: SustainabilityRatingType[]): FilterOption[] {
    const filterOptions = enableFilterMode ? [...this.presetFilterOptions] : [];
    sustainabilityRatingTypes.forEach(ratingType => filterOptions.push({
      value: ratingType.sustainability_rating_type_id,
      label: ratingType.name
    }));
    return filterOptions;
  }
}
